/**
 * 이용권 결제취소(환불) 확인 모달
 * - 결제취소 전에 이용권 상세 정보를 다시 보여주고 확인을 받음
 * - 온라인 결제 + 결제완료 + 미사용 건만 환불 가능 (BE 가 refundable 로 내려줌)
 * - 토스 부분취소로 해당 이용권 1건 금액만 환불됨
 * - API: POST /api/admin/tickets/{purchaseTicketSeq}/refund
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { ticketsApi } from '@/services/api';
import { Modal } from '@/components/ui/Modal';
import {
  LoadingState,
  ErrorState,
  StatusBadge,
  type BadgeTone,
} from '@/components/common';
import { useUI } from '@/hooks/useUI';

interface Props {
  purchaseTicketSeq: number | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const TICKET_STATUS_LABEL: Record<string, string> = {
  ACTIVE: '유효',
  EXHAUSTED: '소진',
  EXPIRED: '만료',
  REVOKED: '회수',
};

const TICKET_STATUS_TONE: Record<string, BadgeTone> = {
  ACTIVE: 'success',
  EXHAUSTED: 'neutral',
  EXPIRED: 'danger',
  REVOKED: 'warning',
};

const PRODUCT_TYPE_LABEL: Record<string, string> = {
  PRODUCT_TYPE_DAYCARE: '데이케어',
  PRODUCT_TYPE_HOTEL: '호텔',
  PRODUCT_TYPE_CLASS: '수업',
  PRODUCT_TYPE_GROOMING: '미용',
  PRODUCT_TYPE_KINDERGARTEN: '유치원',
};

const PAYMENT_TYPE_LABEL: Record<string, string> = {
  ONLINE: '온라인',
  OFFLINE: '오프라인(수동)',
};

/** 라벨-값 한 줄 */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="text-[11px] text-gray-500">{label}</span>
      <span className="text-sm text-gray-900">{children}</span>
    </div>
  );
}

export function TicketRefundModal({ purchaseTicketSeq, onClose, onSuccess }: Props) {
  const open = purchaseTicketSeq != null;
  const [reason, setReason] = useState('');
  const { showSuccess, showError } = useUI();
  const queryClient = useQueryClient();

  // 모달 열릴 때마다 사유 초기화
  useEffect(() => {
    if (open) setReason('');
  }, [open, purchaseTicketSeq]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ticketDetail', purchaseTicketSeq],
    queryFn: () => ticketsApi.getTicketDetail(purchaseTicketSeq!),
    enabled: open,
  });

  const refundMutation = useMutation({
    mutationFn: () =>
      ticketsApi.refundTicket({ purchaseTicketSeq: purchaseTicketSeq!, reason: reason.trim() || undefined }),
    onSuccess: () => {
      showSuccess('결제취소 완료', '이용권 결제가 취소(환불)되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticketDetail', purchaseTicketSeq] });
      onSuccess?.();
      onClose();
    },
    onError: (err: unknown) => {
      const message = (err as { message?: string })?.message ?? '결제취소에 실패했습니다.';
      showError('결제취소 실패', message);
    },
  });

  if (!open) return null;

  const summary = data?.summary;

  return (
    <Modal isOpen={open} onClose={onClose} title="이용권 결제취소(환불)" size="lg">
      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : summary ? (
        <div className="space-y-4">
          {/* 경고 배너 */}
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-red-500" />
            <div className="text-sm text-red-700">
              결제를 취소하면 <strong>{summary.amount.toLocaleString()}원</strong>이 사용자에게
              환불되며 <strong>되돌릴 수 없습니다.</strong> 아래 정보를 확인 후 진행해주세요.
            </div>
          </div>

          {/* 상세 정보 */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <StatusBadge tone={TICKET_STATUS_TONE[summary.ticketStatus]}>
                {TICKET_STATUS_LABEL[summary.ticketStatus]}
              </StatusBadge>
              <span className="text-sm font-semibold text-gray-900">{summary.name}</span>
              <span className="text-xs text-gray-500">{summary.orderId}</span>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-2 md:grid-cols-3">
              <Row label="회원">
                {summary.userName} <span className="text-xs text-gray-500">({summary.userPhone})</span>
              </Row>
              <Row label="펫">{summary.petName ?? '-'}</Row>
              <Row label="사업장">{summary.companyName}</Row>
              <Row label="상품유형">{PRODUCT_TYPE_LABEL[summary.productType] ?? summary.productType}</Row>
              <Row label="결제금액">
                <span className="font-semibold text-gray-900">
                  {summary.amount.toLocaleString()}원
                </span>{' '}
                <span className="text-xs text-gray-500">
                  ({PAYMENT_TYPE_LABEL[summary.paymentType] ?? summary.paymentType})
                </span>
              </Row>
              <Row label="횟수 (잔여/사용/총)">
                {summary.remainingQty} / {summary.usedQty} / {summary.splitCount}
              </Row>
              <Row label="발급일">{summary.dtRegist.slice(0, 10)}</Row>
              <Row label="유효기간">
                {summary.dtUsable.slice(0, 10)} ~ {summary.dtExpired.slice(0, 10)}
              </Row>
              <Row label="결제키">
                <span className="break-all text-xs text-gray-500">
                  {summary.paymentKey ?? '-'}
                </span>
              </Row>
            </div>
          </div>

          {/* 환불 불가 안내 */}
          {!summary.refundable && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {summary.refundableReason ?? '결제취소가 불가능한 이용권입니다.'}
            </div>
          )}

          {/* 환불 사유 + 실행 버튼 */}
          {summary.refundable && (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-600">환불 사유 (선택)</span>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="환불 사유를 입력해주세요. (미입력 시 '관리자 결제취소')"
                rows={3}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </label>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={refundMutation.isPending}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              닫기
            </button>
            {summary.refundable && (
              <button
                type="button"
                onClick={() => refundMutation.mutate()}
                disabled={refundMutation.isPending}
                className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
              >
                {refundMutation.isPending ? '처리 중...' : '결제취소(환불) 실행'}
              </button>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
